/**
 * The brain's page operations — the vocabulary a reconciler pass emits, and the
 * deterministic check the apply layer runs BEFORE it touches the tree.
 *
 * Keyed by PATH, not slug, because the folder is the type (`pageTypeOf`): a
 * page's identity is where it is filed, so an op that could not name a folder
 * could not say what it was making. That is also why there is no `category`
 * field and no `create`/`rewrite` split — the type rides the path, and whether
 * a page already exists is the tree's answer, not something a plan pre-declares.
 *
 * This sits beside the legacy slug-keyed vocabulary rather than replacing it:
 * `wiki-editor-ops.ts` in `@vestra/agent-sdk` still runs the Phase 1 editor
 * pass, and both validate until that pass retires. It lives HERE rather than
 * next to that file because the path grammar it enforces lives here and
 * `@vestra/agent-sdk` deliberately does not depend on `@bash/shared`.
 *
 * Two rules the shape enforces:
 *   - Content lands only on typed paths. A source may still be a legacy flat
 *     page (read-only compat until the filing migration), so a page can be
 *     read, moved and retired out of `knowledge/pages/` — never written into it.
 *   - In one plan a page is written at most once and retired at most once, and
 *     never both — with one exception that is not really one: two merges may
 *     fold into a single survivor, because a merge adds to a page rather than
 *     replacing it. Everything else that would write a page twice, or write and
 *     retire the same page, is refused, an op that is its own source and
 *     destination included. A plan that says two things about one page says
 *     nothing reliable about it.
 */

import { isLegacyFlatPage, pagePathRe } from './memory-tree.js';

/**
 * `move` is a git mv — the page's history follows it. It is how the filing
 * migration files a flat page into its folder, and how a later rename lands.
 */
export type BrainPageOp =
  | { op: 'write'; path: string; body: string }
  | { op: 'merge'; into: string; from: string }
  | { op: 'delete'; path: string }
  | { op: 'move'; from: string; to: string };

export interface BrainPagePlan {
  ops: BrainPageOp[];
}

/** The closed op vocabulary — anything else is rejected before any other check. */
const OPS = new Set<string>(['write', 'merge', 'delete', 'move']);

export function validateBrainPageOps(
  plan: BrainPagePlan,
  existingPaths: string[],
): { valid: BrainPageOp[]; rejected: { op: BrainPageOp; reason: string }[] } {
  // The tree as it stands before the plan runs. Existence is judged against
  // that snapshot, never against what an earlier op in the same plan would
  // have created — so a plan is judged whole, and rejecting an op never
  // silently changes the verdict on the ones behind it.
  const existing = new Set(existingPaths);
  // Every path this plan puts content at: write targets, move destinations, and
  // a merge's survivor. A `write` and a `move` additionally CLAIM their
  // destination, so a second op landing content there is a collision; a `merge`
  // does not claim, because a preceding write may already own that page and two
  // merges may fold into one survivor. Both are read off this one set: what a
  // claim may not collide with is anything already being written, merges
  // included — a write over a merged page discards what was folded in, and the
  // page it came from is already retired.
  const written = new Set<string>();
  const retired = new Set<string>();
  const valid: BrainPageOp[] = [];
  const rejected: { op: BrainPageOp; reason: string }[] = [];

  // The plan is model-emitted JSON, so `ops` is not known to be a list until it
  // is checked. Absent is an empty plan (a pass with nothing to say); present
  // and not a list is a malformed one, and says so rather than throwing.
  if (plan?.ops != null && !Array.isArray(plan.ops)) {
    return { valid, rejected: [{ op: plan.ops as BrainPageOp, reason: 'ops is not a list' }] };
  }

  for (const op of plan?.ops ?? []) {
    // Closed vocabulary FIRST: a malformed op, or one from the legacy
    // slug-keyed vocabulary, is rejected before any field of it is read.
    if (!op || typeof op !== 'object' || !OPS.has((op as { op?: unknown }).op as string)) {
      rejected.push({ op, reason: 'unknown op' });
      continue;
    }

    // Which paths an op carries is read off its KIND, never off whether a field
    // happens to be null — a null smuggled into the payload has to arrive as a
    // bad path, not as an absent one.
    const destinations: string[] =
      op.op === 'write' ? [op.path] : op.op === 'move' ? [op.to] : op.op === 'merge' ? [op.into] : [];
    const sources: string[] =
      op.op === 'delete' ? [op.path] : op.op === 'move' || op.op === 'merge' ? [op.from] : [];
    /** The destination a write or a move takes exclusively; a merge takes none. */
    const claim = op.op === 'write' || op.op === 'move' ? destinations[0]! : undefined;

    // Every path the op carries has to BE a path before any rule can read it.
    // This gate is what lets the checks below use `find`: a missing field is
    // `undefined`, and `find` returning the element it found would be
    // indistinguishable from `find` reporting no match — so an op with no
    // `path` at all would validate. `findIndex` is the only way to ask.
    const paths = [...destinations, ...sources];
    const malformed = paths.findIndex((p) => typeof p !== 'string' || p.length === 0);
    if (malformed !== -1) {
      rejected.push({ op, reason: `not a page path: ${paths[malformed]}` });
      continue;
    }

    if (op.op === 'write' && (typeof op.body !== 'string' || op.body.length === 0)) {
      // The body is what lands on disk. A page written from `undefined` is a
      // page destroyed; whether its prose carries citations is C5's question.
      rejected.push({ op, reason: `write with no body: ${op.path}` });
      continue;
    }

    const badDestination = destinations.find((p) => !pagePathRe.test(p));
    if (badDestination !== undefined) {
      rejected.push({ op, reason: `not a page path: ${badDestination}` });
      continue;
    }
    const badSource = sources.find((p) => !pagePathRe.test(p) && !isLegacyFlatPage(p));
    if (badSource !== undefined) {
      rejected.push({ op, reason: `not a page path: ${badSource}` });
      continue;
    }

    // One page on both ends of one op: a self-merge folds a page into itself and
    // retires it, writing and retiring the same page in a single op. Every other
    // check passes it — the page is typed, and it exists on both sides.
    const selfReference = sources.find((p) => destinations.includes(p));
    if (selfReference !== undefined) {
      rejected.push({ op, reason: `${selfReference} is both the source and the destination` });
      continue;
    }

    const missingSource = sources.find((p) => !existing.has(p));
    if (missingSource !== undefined) {
      rejected.push({ op, reason: `${op.op} of a page that does not exist: ${missingSource}` });
      continue;
    }
    if (op.op === 'merge' && !existing.has(op.into)) {
      rejected.push({ op, reason: `merge into a page that does not exist: ${op.into}` });
      continue;
    }
    // Moving onto a page overwrites it, and nothing says so. Folding two pages
    // into one is what `merge` is for.
    if (op.op === 'move' && existing.has(op.to)) {
      rejected.push({ op, reason: `move onto an existing page: ${op.to}` });
      continue;
    }

    if (claim !== undefined && written.has(claim)) {
      rejected.push({ op, reason: `duplicate op for ${claim}` });
      continue;
    }
    const contested = destinations.find((p) => retired.has(p));
    if (contested !== undefined) {
      rejected.push({ op, reason: `${contested} is being retired in this plan` });
      continue;
    }
    const reRetired = sources.find((p) => retired.has(p));
    if (reRetired !== undefined) {
      rejected.push({ op, reason: `duplicate retirement of ${reRetired}` });
      continue;
    }
    const stillWritten = sources.find((p) => written.has(p));
    if (stillWritten !== undefined) {
      rejected.push({ op, reason: `${stillWritten} is being written in this plan` });
      continue;
    }

    for (const p of destinations) written.add(p);
    for (const p of sources) retired.add(p);
    valid.push(op);
  }

  return { valid, rejected };
}
